import { Fragment, FunctionComponent, RefObject, useCallback, useEffect, useRef, useState } from 'react'
import styles from './mobileAgendaView.module.scss'
import { DayPicker, Matcher } from 'react-day-picker'
import { getNextOpeningDate } from '@/modules/helper/daycareHelper.ts'
import { Daycare } from '@/types/Daycare.ts'
import { EventCalendar } from '@/types/Calendar.ts'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import { useTranslation } from 'react-i18next'
import {
    ArrowBack,
    ArrowDownward,
    ArrowForward,
    ArrowUpward,
    KeyboardArrowDown,
    KeyboardArrowUp
} from '@mui/icons-material'
import {
    compareEventsByTypeForAgendaViewDaycare,
    compareEventsByTypeForAgendaViewParent,
    renderEventAgendaView
} from '../../../utils/calendarEvents.tsx'
import ButtonIcon from '@/components/_custom/Buttons/ButtonIcon/ButtonIcon.tsx'
import { getLocalFromLanguage, getTextFromLanguage } from '@/modules/helper/languageHelper.ts'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { format, parse } from 'date-fns'
import moment, { Moment } from 'moment-timezone'
import { useAgendaEvents } from './hooks/useAgendaEvents.ts'
import { useHiddenDays } from './hooks/useHiddenDays.ts'
import { useAgendaScroll } from './hooks/useAgendaScroll.ts'
import { FetchPlanningDataProps } from '@/container/planning/hooks/usePlanningSlots.ts'

interface MobileAgendaViewProps {
    currentDaycare: Daycare
    setSelectedDateForOtherView: (date: Moment) => void
    calendarEvents: EventCalendar[]
    isParent: boolean
    disabledDay: Matcher[]
    fetchData: (props: FetchPlanningDataProps) => void
    handleEventClick: (info: EventCalendar) => void
    isGlobalPlanning: boolean
}

const MobileAgendaView: FunctionComponent<MobileAgendaViewProps> = ({
    currentDaycare,
    setSelectedDateForOtherView,
    calendarEvents,
    disabledDay,
    isParent,
    fetchData,
    handleEventClick,
    isGlobalPlanning
}: MobileAgendaViewProps) => {
    const { t } = useTranslation()
    const navigate: NavigateFunction = useNavigate()

    const [selectedDate, setSelectedDate] = useState<Moment>(getNextOpeningDate(currentDaycare, moment()))
    const [hiddenEventDays, setHiddenEventDays] = useState<{ [key: string]: boolean }>({})

    const eventRefs: RefObject<{ [key: string]: HTMLDivElement | null }> = useRef<{
        [key: string]: HTMLDivElement | null
    }>({})
    const eventListRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)
    const dayPickerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)

    useEffect(() => {
        moment.locale(getTextFromLanguage())
    }, [])

    const fetchDataMemoized: (fromDate: Moment, toDate: Moment, isGlobalPlanning: boolean) => void = useCallback(
        (fromDate: Moment, toDate: Moment, isGlobalPlanning: boolean): void => {
            fetchData({ fromDate, toDate, isGlobalPlanning })
        },
        [fetchData, isGlobalPlanning]
    )

    function refetchData(date: Moment): void {
        fetchDataMemoized(moment(date).startOf('month'), moment(date).endOf('month'), isGlobalPlanning)
    }

    useEffect(() => {
        refetchData(selectedDate)
    }, [currentDaycare, isGlobalPlanning])

    const { hiddenDays, toggleHiddenDays, hideDaysWithDate, restoreRows } = useHiddenDays(selectedDate, dayPickerRef)

    const { agendaEvents, groupedEvents, agendaDate } = useAgendaEvents({
        calendarEvents,
        selectedDate,
        onGroupedEventsReady: () => {}
    })

    useAgendaScroll({
        agendaEvents,
        groupedEvents,
        selectedDate,
        hiddenDays,
        eventListRef,
        eventRefs,
        setSelectedDate,
        setSelectedDateForOtherView,
        hideDaysWithDate
    })

    function agendaDayClick(day: Date | undefined): void {
        let newSelectedDate: Moment = moment(day)

        const hasEventOnDay: boolean =
            agendaEvents.filter(
                (event: EventCalendar) =>
                    moment(event.start).format('YYYY-MM-DD') === newSelectedDate.format('YYYY-MM-DD')
            ).length > 0

        if (!hasEventOnDay) {
            const futureEvents: EventCalendar[] = agendaEvents.filter((e: EventCalendar) =>
                moment(e.start).isAfter(newSelectedDate, 'day')
            )
            const previousEvents: EventCalendar[] = agendaEvents.filter((e: EventCalendar) =>
                moment(e.start).isBefore(newSelectedDate, 'day')
            )

            if (futureEvents.length > 0) {
                const nextEvent: EventCalendar = futureEvents.reduce((acc, e) =>
                    moment(e.start).isBefore(acc.start) ? e : acc
                )
                newSelectedDate = newSelectedDate.clone().set('date', moment(nextEvent.start).date())
            } else if (previousEvents.length > 0) {
                const previousEvent: EventCalendar = previousEvents.reduce((acc, e) =>
                    moment(e.start).isAfter(acc.start) ? e : acc
                )
                newSelectedDate = newSelectedDate.clone().set('date', moment(previousEvent.start).date())
            }
        }

        setSelectedDate(newSelectedDate)
        setSelectedDateForOtherView(newSelectedDate)
    }

    function previousMonth(): void {
        restoreRows()
        const prev: Moment = moment(selectedDate).subtract(1, 'month').startOf('month')
        setSelectedDate(getNextOpeningDate(currentDaycare, prev))
        refetchData(prev)
    }

    function nextMonth(): void {
        restoreRows()
        const next: Moment = moment(selectedDate).add(1, 'month').startOf('month')
        setSelectedDate(getNextOpeningDate(currentDaycare, next))
        refetchData(next)
    }

    function onMonthChange(month: Date): void {
        if (moment(month).isBefore(moment(selectedDate).startOf('month'))) {
            previousMonth()
        } else {
            nextMonth()
        }
    }

    function toggleEventsVisibility(date: string): void {
        setHiddenEventDays((prev) => ({ ...prev, [date]: !prev[date] }))
    }

    function selectEvent(event: EventCalendar): void {
        if (isGlobalPlanning) {
            navigate(`/drafting?id=${event.sectionGroupId}&date=${moment(event.start).format('DD-MM-YYYY')}`)
        } else {
            handleEventClick(event)
        }
    }

    return (
        <div className={`${styles.Container} ${isParent ? styles.ContainerParent : ''}`}>
            <div className={styles.Agenda}>
                <div ref={dayPickerRef}>
                    <DayPicker
                        disabled={disabledDay}
                        mode='single'
                        required={false}
                        month={selectedDate.toDate()}
                        onMonthChange={onMonthChange}
                        selected={selectedDate.toDate()}
                        onSelect={agendaDayClick}
                        locale={getLocalFromLanguage()}
                        modifiers={{
                            events: agendaDate,
                            hideDays: hiddenDays
                        }}
                        modifiersClassNames={{
                            selected: isParent ? styles.SelectedDayParent : styles.SelectedDayDaycare,
                            events: `${styles.AgendaDot} ${isParent ? styles.AgendaDotParent : styles.AgendaDotDaycare}`,
                            hideDays: styles.HiddenDay,
                            today: styles.Today
                        }}
                        className={isParent ? styles.DayPickerParent : styles.DayPickerDaycare}
                    />
                </div>
                <ButtonIcon
                    icon={hiddenDays.length > 0 ? <ArrowDownward /> : <ArrowUpward />}
                    className={isParent ? styles.IconButtonParent : styles.IconButtonDaycare}
                    onClick={toggleHiddenDays}
                    parent={isParent}
                />
            </div>
            <div ref={eventListRef} className={styles.EventList}>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        onClick={previousMonth}
                        customText={t('previousMonth')}
                        customIcon={<ArrowBack />}
                        className={styles.Button}
                        parent={isParent}
                    />
                </div>
                {Object.keys(groupedEvents).length === 0 ? (
                    <div className={styles.NoEvent}>{t('nothingThisMonth')}</div>
                ) : (
                    Object.entries(groupedEvents)
                        .sort(([dateA], [dateB]): number => {
                            if (dateA < dateB) return -1
                            if (dateA > dateB) return 1
                            return 0
                        })
                        .map(([date, events]) => (
                            <div
                                key={date}
                                className={styles.EventGroup}
                                ref={(el: HTMLDivElement | null) => {
                                    eventRefs.current[date] = el
                                }}
                            >
                                <div className={styles.EventDate} onClick={() => toggleEventsVisibility(date)}>
                                    {format(parse(date, 'yyyy-MM-dd', new Date()), 'EEEE dd MMMM yyyy', {
                                        locale: getLocalFromLanguage()
                                    })}
                                    {hiddenEventDays[date] ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
                                </div>
                                {!hiddenEventDays[date] &&
                                    [...events]
                                        .sort(
                                            isParent
                                                ? compareEventsByTypeForAgendaViewParent
                                                : compareEventsByTypeForAgendaViewDaycare
                                        )
                                        .map((event: EventCalendar, index: number) => (
                                            <Fragment key={`${index}_${event.type}`}>
                                                {renderEventAgendaView(event, selectEvent)}
                                            </Fragment>
                                        ))}
                            </div>
                        ))
                )}
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'custom'}
                        onClick={nextMonth}
                        customText={t('nextMonth')}
                        customIcon={<ArrowForward />}
                        className={styles.Button}
                        parent={isParent}
                    />
                </div>
            </div>
        </div>
    )
}

export default MobileAgendaView
